import * as Yup from 'yup';

const objectIdRegex = /^[a-f\d]{24}$/i;

const defaultInventoryItem = {
    property_id: '',
    building_name: '',
    items: [
        {
            name: '',
            make: '',
            modelNo: '',
            sku: [],
            requestsSupported: [],
            quantity: {
                active: '',
                min: '',
                max: '',
            },
            costPerUnit: '',
            warrantyDetails: [
                {
                    boughtOn: undefined,
                    boughtFrom: undefined,
                    warrantyExpirationDate: '',
                },
            ],
            locationDetails: {
                room: '',
                notes: '',
            },
            images: [
                {
                    url: '',
                    uploadedBy: {
                        id: '',
                        role: '',
                    },
                },
            ],
            invoices: [
                {
                    url: '',
                    uploadedBy: {
                        id: '',
                        role: '',
                    },
                },
            ],
            status: '',
        },
    ],
};

export const inventoryItemSchema = Yup.object().shape({
    property_id: Yup.string()
        .test('is-object-id', 'Invalid property ID', value => !value || objectIdRegex.test(value))
        .required('Property ID is required'),
    building_name: Yup.string().required('Building name is required'),
    items: Yup.array()
        .of(
            Yup.object().shape({
                name: Yup.string().required('Item name is required'),
                make: Yup.string().optional(),
                modelNo: Yup.string().optional(),
                sku: Yup.array().of(Yup.string()).optional(),
                requestsSupported: Yup.array()
                    .of(
                        Yup.string().test('is-object-id', 'Invalid request type ID', value => !value || objectIdRegex.test(value))
                    )
                    .min(1, 'At least one request type is required')
                    .required('Requests supported are required'),
                quantity: Yup.object().shape({
                    active: Yup.number()
                        .required('Active quantity is required')
                        .min(0, 'Active quantity cannot be negative'),
                    min: Yup.number()
                        .optional()
                        .min(0, 'Minimum quantity cannot be negative'),
                    max: Yup.number()
                        .optional()
                        .min(0, 'Maximum quantity cannot be negative'),
                }).required('Quantity details are required'),
                costPerUnit: Yup.number()
                    .required('Cost per unit is required')
                    .min(0, 'Cost cannot be negative'),
                warrantyDetails: Yup.array()
                    .of(
                        Yup.object().shape({
                            boughtOn: Yup.date().optional(),
                            boughtFrom: Yup.string().optional(),
                            warrantyExpirationDate: Yup.date().optional(),
                        })
                    )
                    .optional(),
                locationDetails: Yup.object().shape({
                    room: Yup.string().optional(),
                    notes: Yup.string().optional(),
                }).optional(),
                images: Yup.array()
                    .of(
                        Yup.object().shape({
                            url: Yup.string().url('Invalid image URL').optional(),
                            uploadedBy: Yup.object()
                                .shape({
                                    id: Yup.string()
                                        .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                                        .optional(),
                                    role: Yup.string().optional(),
                                })
                                .optional(),
                        })
                    )
                    .optional(),
                invoices: Yup.array()
                    .of(
                        Yup.object().shape({
                            url: Yup.string().url('Invalid invoice URL').optional(),
                            uploadedBy: Yup.object()
                                .shape({
                                    id: Yup.string()
                                        .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                                        .optional(),
                                    role: Yup.string().optional(),
                                })
                                .optional(),
                        })
                    )
                    .optional(),
                status: Yup.string().optional(),
            })
        )
        .min(1, 'At least one item is required'),
});

export const updateInventoryItemSchema = Yup.object().shape({
    item_id: Yup.string()
        .test('is-object-id', 'Invalid item ID', value => !value || objectIdRegex.test(value))
        .required('Item ID is required'),
    item: Yup.object().shape({
        name: Yup.string().optional(),
        make: Yup.string().optional(),
        modelNo: Yup.string().optional(),
        sku: Yup.array().of(Yup.string()).optional(),
        requestsSupported: Yup.array()
            .of(
                Yup.string().test('is-object-id', 'Invalid request type ID', value => !value || objectIdRegex.test(value))
            )
            .optional(),
        quantity: Yup.object()
            .shape({
                active: Yup.number()
                    .optional()
                    .min(0, 'Active quantity cannot be negative'),
                min: Yup.number()
                    .optional()
                    .min(0, 'Minimum quantity cannot be negative'),
                max: Yup.number()
                    .optional()
                    .min(0, 'Maximum quantity cannot be negative'),
            })
            .optional(),
        costPerUnit: Yup.number()
            .optional()
            .min(0, 'Cost cannot be negative'),
        warrantyDetails: Yup.array()
            .of(
                Yup.object().shape({
                    boughtOn: Yup.date().nullable(),
                    boughtFrom: Yup.string().nullable(),
                    warrantyExpirationDate: Yup.date().nullable(),
                })
            )
            .optional(),
        locationDetails: Yup.object()
            .shape({
                room: Yup.string().optional(),
                notes: Yup.string().optional(),
            })
            .optional(),
        images: Yup.array()
            .of(
                Yup.object().shape({
                    url: Yup.string().url('Invalid image URL').optional(),
                    uploadedBy: Yup.object()
                        .shape({
                            id: Yup.string()
                                .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                                .optional(),
                            role: Yup.string().optional(),
                        })
                        .optional(),
                })
            )
            .optional(),
        invoices: Yup.array()
            .of(
                Yup.object().shape({
                    url: Yup.string().url('Invalid invoice URL').optional(),
                    uploadedBy: Yup.object()
                        .shape({
                            id: Yup.string()
                                .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                                .optional(),
                            role: Yup.string().optional(),
                        })
                        .optional(),
                })
            )
            .optional(),
        status: Yup.string().optional(),
    }),
    property_id: Yup.string()
        .test('is-object-id', 'Invalid property ID', value => !value || objectIdRegex.test(value))
        .optional(),
    building_name: Yup.string().optional(),
});

export const getValidationSchema = (modal) => {
    if (modal === 'add') {
        return inventoryItemSchema;
    } else if (modal === 'edit') {
        return updateInventoryItemSchema;
    }
    return inventoryItemSchema;
};

export const getInitialValues = (modal, selectedItem, preservedValues = {}) => {
    if (modal === 'add') {
        return {
            ...defaultInventoryItem,
            ...preservedValues,
        };
    } else if (modal === 'edit' && selectedItem) {
        return {
            item_id: selectedItem._id || '',
            item: {
                name: selectedItem.name || '',
                make: selectedItem.make || '',
                modelNo: selectedItem.modelNo || '',
                sku: selectedItem.sku || [],
                requestsSupported: Array.isArray(selectedItem.requestsSupported)
                    ? selectedItem.requestsSupported.map(item =>
                        typeof item === 'object' && item._id ? item._id : item
                    )
                    : [],
                quantity: {
                    active: selectedItem.quantity?.active || '',
                    min: selectedItem.quantity?.min || '',
                    max: selectedItem.quantity?.max || '',
                },
                costPerUnit: selectedItem.costPerUnit || '',
                warrantyDetails: selectedItem.warrantyDetails || [
                    {
                        boughtOn: undefined,
                        boughtFrom: undefined,
                        warrantyExpirationDate: '',
                    },
                ],
                locationDetails: {
                    room: selectedItem.locationDetails?.room || '',
                    notes: selectedItem.locationDetails?.notes || '',
                },
                images: selectedItem.images || [],
                invoices: selectedItem.invoices || [],
                status: selectedItem.status || '',
            },
            property_id: preservedValues.property_id || selectedItem.property_id || '',
            building_name: preservedValues.building_name || selectedItem.building_name || '',
        };
    }

    return {
        ...defaultInventoryItem,
        ...preservedValues,
    };
};